import {
  Grid, Typography, makeStyles, Box,
  Checkbox, FormControlLabel, CircularProgress,
} from "@material-ui/core";
import { useContext, useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from 'react-redux';

import QCXFinalPercentualField from '../../../../../shared-components/final-percentual-field/QCXFinalPercentualField';
import QCXTextField from '../../../../../shared-components/text-field/QCXTextField';

import { unnormalizeNumeral, formatDate } from '../../../../../utils/general/general-utils';
import { formatBrazilianNumericDecimal } from '../../../../../utils/hooks/form/field/formatters';

import DeclaracaoImportacaoContext from '../../../../../contexts/import/declaracao-importacao/DeclaracaoImportacaoContext';
import { selectDeclaracaoImportacao, selectSubMode, selectRelatedAdicaoModel } from '../../../../../features/declaracao-importacao/declaracaoImportacaoSelectors';
import QCXFormStepWizard from '../../../../../shared-components/form-step-wizard/QCXFormStepWizard';
import QCXCustomRegistrationTabControlTemplate from '../../../../../templates/custom-registration-one-to-many-page/QCXCustomRegistrationTabControlTemplate';

import {
  fetchAdicoesByDiDuimpIdAsync,
} from '../../../../../features/declaracao-importacao/declaracaoImportacaoThunks';

import {
  changeToSubConsultMode,
  resetRelatedAdicaoModel,
  resetSubMode,
  resetSubBackgroundMode,
  resetStatus,
  setRelatedAdicaoModel,
  success,
  loading,
} from '../../../../../features/declaracao-importacao/declaracaoImportacaoSlice';

import { setErrorFeedback } from '../../../../../features/feedback/feedbackSlice';
import { doGetQCXRequest } from '../../../../../utils/api/domain/domain-api-utils';
import { isNoneSubMode, isSubConsultMode, isSubCreateMode, isSubUpdateMode } from '../../../../../utils/store/store-utils';
import { BRAZILIAN } from '../../../../../utils/hooks/form/field/mask-types';

import QCXFinalAlternativeCurrencyField from '../../../../../shared-components/final-currency-field/QCXFinalAlternativeCurrencyField';
import QCXFormTitle from '../../../../../components/form-title/QCXFormTitle';
import QCXSelectRegimeTributacaoAutocomplete from '../../../../../shared-components/select-regime-tributacao/QCXSelectRegimeTributacaoAutocomplete';
import QCXSelectIncotermCondicaoVendaAutocomplete from '../../../../../shared-components/final-select-incoterm-condicao-venda-field/QCXSelectIncotermCondicaoVendaAutocomplete';
import { fetchAllAsync as fetchAllMoedasAsync, selectMoedas } from '../../../../../features/moeda/moedaSlice';


const fmtR = (val) => unnormalizeNumeral(val ?? 0, formatBrazilianNumericDecimal(2));
const fmtQ = (val) => unnormalizeNumeral(val ?? 0, formatBrazilianNumericDecimal(7));


const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: '80%',
    width: 800,
    '@media (max-width: 900px)': {
      maxWidth: '100%',
      width: '100%',
    },
  },
  title: {
    borderBottom: '2px solid #000000',
  },
  groupBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    height: '100%',
  },
  groupTitle: {
    fontWeight: 700,
    fontSize: 13,
    textTransform: 'uppercase',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(1),
    paddingBottom: 4,
    color: theme.palette.primary.main,
  },
}));


// Calls form.initialize imperatively when the selected adição changes, bypassing
// React Final Form's prop-change detection which is unreliable across versions.
const FormReinitializer = ({ form, initialValues, adicaoId }) => {
  useEffect(() => {
    if (adicaoId) {
      form.initialize(initialValues);
    }
  }, [adicaoId]);
  return null;
};

const AdicaoResumoFormStepWizard = ({ formPaginationControlProps = {}, ...restProps }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const di = useSelector(selectDeclaracaoImportacao);
  const subMode = useSelector(selectSubMode);
  const adicao = useSelector(selectRelatedAdicaoModel) || {};
  const moedas = useSelector(selectMoedas);
  const intl = BRAZILIAN;

  const {
    idDiSelecionada: idDuimp,
    unnormalize,
  } = useContext(DeclaracaoImportacaoContext);

  const hasFetchedMercadoriasRef = useRef(false);
  const [isLoadingAdicaoDetail, setIsLoadingAdicaoDetail] = useState(false);
  const adicoes = useMemo(() => di?.atributosAdicionais?.adicoes, [di]);

  const initialValues = useMemo(() => (adicao ? unnormalize(adicao) : {}), [adicao, unnormalize]);

  useEffect(() => {
    dispatch(fetchAllMoedasAsync());
  }, []);

  useEffect(() => {
    if (!adicoes?.length && !hasFetchedMercadoriasRef.current && idDuimp) {
      hasFetchedMercadoriasRef.current = true;
      dispatch(fetchAdicoesByDiDuimpIdAsync(idDuimp));
    }
  }, [idDuimp]);

  const fetchAdicaoDetail = useCallback(async ({ id }) => {
    if (!id) return;
    try {
      setIsLoadingAdicaoDetail(true);
      const response = await doGetQCXRequest(['diduimp', 'adicoes-detail', id]);
      dispatch(setRelatedAdicaoModel(response.data));
      dispatch(success());
    } catch (error) {
      dispatch(setErrorFeedback({ message: 'Erro ao buscar adição.' }));
    } finally {
      setIsLoadingAdicaoDetail(false);
    }
  }, []);

  const getMoedaLabel = useCallback((moedaOrId) => {
    const id = moedaOrId && typeof moedaOrId === 'object' ? moedaOrId?.id : moedaOrId;
    if (!id) return '-';
    const m = moedas.find((x) => x.id === id || x.id === Number(id));
    return m ? `${String(m.code).padStart(3, '0')} - ${m.description}` : String(id);
  }, [moedas]);

  const ncmLabel = useMemo(() => {
    if (!adicao?.ncm?.code) return '-';
    return adicao.ncm.description
      ? `${adicao.ncm.code} - ${adicao.ncm.description}`
      : adicao.ncm.code;
  }, [adicao?.ncm]);

  const dataCalculoLabel = useMemo(() => {
    const date = di?.data ?? di?.atributosAdicionais?.dataRegistroSiscomex;
    return date ? formatDate(date) : '-';
  }, [di]);

  const currentTitleSubPage = useMemo(() => t('com.muralis.qcx.adicao.dadosAdicao'), []);

  const tableProps = useMemo(
    () => ({
      title: t('com.muralis.qcx.adicao.adicoesAdicionadas'),
      columns: [
        {
          field: 'nroAdicao',
          headerName: 'Ad',
          headerAlign: 'center',
          align: 'center',
          flex: 120,
          valueGetter: ({ row }) => row?.item || '-',
        },
        {
          field: 'quantidadeItens',
          headerName: 'Qt It',
          headerAlign: 'center',
          align: 'center',
          flex: 120,
          valueGetter: ({ row }) => row?.mercadorias?.length || '-',
        },
        {
          field: 'ncm',
          headerName: 'Ncm',
          headerAlign: 'center',
          align: 'center',
          flex: 160,
          valueGetter: ({ row }) => row?.ncm?.code || '-',
        },
        {
          field: 'numeroLi',
          headerName: 'Nro. LI',
          headerAlign: 'center',
          align: 'center',
          flex: 160,
          valueGetter: ({ row }) => row?.li?.registro || row?.mercadorias?.[0]?.numeroLI || '-',
        },
        {
          field: 'ii',
          headerName: 'II',
          headerAlign: 'center',
          align: 'center',
          flex: 140,
          valueFormatter: ({ row }) => unnormalizeNumeral(row?.ii?.aliquota, formatBrazilianNumericDecimal(2)) || '-',
        },
        {
          field: 'ipi',
          headerName: 'IPI',
          headerAlign: 'center',
          align: 'center',
          flex: 140,
          valueFormatter: ({ row }) => unnormalizeNumeral(row?.ipi?.aliquota, formatBrazilianNumericDecimal(2)) || '-',
        },
        {
          field: 'pis',
          headerName: 'PIS',
          headerAlign: 'center',
          align: 'center',
          flex: 140,
          valueFormatter: ({ row }) =>
            unnormalizeNumeral(row?.pisCofins?.aliquotaPisPasep, formatBrazilianNumericDecimal(2)) || '-',
        },
        {
          field: 'cofins',
          headerName: 'COFINS',
          headerAlign: 'center',
          align: 'center',
          flex: 160,
          valueFormatter: ({ row }) =>
            unnormalizeNumeral(row?.pisCofins?.aliquotaCofins, formatBrazilianNumericDecimal(2)) || '-',
        },
        {
          field: 'icms',
          headerName: 'ICMS',
          headerAlign: 'center',
          align: 'center',
          flex: 140,
          valueFormatter: ({ row }) => unnormalizeNumeral(row?.icms?.aliquota, formatBrazilianNumericDecimal(2)) || '-',
        },
        {
          field: 'aplicacaoMercadoria',
          headerName: 'Aplicação',
          headerAlign: 'center',
          align: 'center',
          flex: 200,
          valueFormatter: ({ row }) => row?.aplicacaoMercadoria || '-',
        },
      ],
    }),
    []
  );

  const internalControlButtonsPaginationProps = useMemo(
    () => ({
      paginationInfoField: {
        label: t('com.muralis.qcx.adicao.label'),
      },
      buttonGroup: {
        first: { description: t('com.muralis.qcx.adicao.primeiraAdicao') },
        next: { description: t('com.muralis.qcx.adicao.proximaAdicao') },
        previous: { description: t('com.muralis.qcx.adicao.adicaoAnterior') },
        last: { description: t('com.muralis.qcx.adicao.ultimaAdicao') },
      },
    }),
    [formPaginationControlProps]
  );

  const localeTableText = useMemo(
    () => ({ noRowsLabel: t('com.muralis.qcx.validacao.nenhumaAdicaoEncontrada') }),
    []
  );

  const handleChangeToSubNone = () => { dispatch(resetSubMode()); };
  const handleLoadingStatus = useCallback(() => dispatch(loading()), []);
  const handleChangeToSubConsult = useCallback(() => {
    dispatch(resetSubBackgroundMode());
    dispatch(changeToSubConsultMode());
  }, []);
  const handleResetStatus = useCallback(() => dispatch(resetStatus()), []);
  const handleClearForm = useCallback(() => { dispatch(resetRelatedAdicaoModel()); }, []);

  const isSubNone = useMemo(() => isNoneSubMode(subMode), [subMode]);
  const isSubCreate = useMemo(() => isSubCreateMode(subMode), [subMode]);
  const isSubConsult = useMemo(() => isSubConsultMode(subMode), [subMode]);
  const isSubUpdate = useMemo(() => isSubUpdateMode(subMode), [subMode]);

  const modes = useMemo(
    () => ({ isSubConsult, isSubNone, isSubCreate, isSubUpdate }),
    [isSubConsult, isSubNone]
  );

  return (
    <>
      <QCXFormStepWizard
        initialValues={initialValues}
        onSubmit={() => null}
        disableSubmitOnNext
        disablePristine
        controlComponentProps={{ isConsult: true, isLastStep: true, isLastRegistrationStep: true }}
        {...restProps}
      >
        {({ form, values, submitting, submitSucceeded }) => (
          <>
            <FormReinitializer form={form} initialValues={initialValues} adicaoId={adicao?.id} />
            <QCXCustomRegistrationTabControlTemplate
              initialValues={initialValues}
              title={currentTitleSubPage}
              tableProps={tableProps}
              tableData={adicoes}
              handleEdit={() => null}
              handleChangeModel={fetchAdicaoDetail}
              controlButtonsPaginationProps={internalControlButtonsPaginationProps}
              buttonGroupOptions={{}}
              hiddenNewButton
              isConsult
              disableUpdate
              autoControlInitialValues
              localeTableText={localeTableText}
              handleChangeToSubNone={handleChangeToSubNone}
              handleLoadingStatus={handleLoadingStatus}
              handleChangeToSubConsult={handleChangeToSubConsult}
              handleResetStatus={handleResetStatus}
              handleClearForm={handleClearForm}
            >
              {() => isLoadingAdicaoDetail ? (
                <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <CircularProgress />
                </Grid>
              ) : (
                <>

                  {/* ── BLOCO 1 — DADOS GERAIS ── */}
                  <Grid item xs={12}>
                    <QCXFormTitle title="Dados Gerais" boxProps={{ pt: 0 }} />
                  </Grid>

                  {/* Linha 1: NCM | Destaque | Peso Líquido | Peso Bruto */}
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-ncm-sh"
                      label="NCM SH"
                      value={ncmLabel}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-destaque-ncm"
                      label="Destaque do NCM"
                      value={adicao?.destaqueNcm || '-'}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-peso-liquido"
                      label={t('com.muralis.qcx.pesoLiquido')}
                      value={fmtQ(adicao?.pesoLiquido)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-peso-bruto"
                      label={t('com.muralis.qcx.pesoBruto')}
                      value={fmtQ(adicao?.pesoBruto)}
                      disabled
                    />
                  </Grid>

                  {/* Linha 2: Moeda MLE | Taxa Moeda | Taxa Dólar | Data de Cálculo */}
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-moeda-mle"
                      label="Moeda MLE"
                      value={getMoedaLabel(adicao?.moeda)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-taxa-moeda"
                      label="Taxa Moeda"
                      value={fmtQ(adicao?.taxaMoeda)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-taxa-dolar"
                      label="Taxa Dólar"
                      value={fmtQ(adicao?.taxaDolar)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-data-calculo"
                      label="Data de Cálculo"
                      value={dataCalculoLabel}
                      disabled
                    />
                  </Grid>


                  {/* ── BLOCO 2 — VALORES FINANCEIROS ── */}
                  <Grid item xs={12}>
                    <QCXFormTitle title="Valores Financeiros" />
                  </Grid>

                  {/* Linha 3: Valor Total MLE (sem moeda) */}
                  <Grid item xs={12} sm={6} md={4}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-mle-moeda"
                      name="valorMleMoeda"
                      label="Valor Total MLE na Moeda"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-mle-dolar"
                      name="valorMleDolar"
                      label="Valor Total MLE em Dólar"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-mle-real"
                      name="valorMleReal"
                      label="Valor Total MLE em Reais"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>

                  {/* Linha 4: Despesas */}
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-moeda-despesas"
                      label="Moeda das Despesas"
                      value={getMoedaLabel(adicao?.moeda)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-despesa-moeda"
                      name="valorDespesaMoeda"
                      label="Valor das Despesas na Moeda"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-despesa-dolar"
                      name="valorDespesaDolar"
                      label="Valor das Despesas em Dólar"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-despesa-real"
                      name="valorDespesaReal"
                      label="Valor das Despesas em Reais"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>

                  {/* Linha 5: Frete */}
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-moeda-frete"
                      label="Moeda Frete"
                      value={getMoedaLabel(adicao?.moeda)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-frete-moeda"
                      name="valorFreteMoeda"
                      label="Valor Total do Frete Internacional na Moeda"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-frete-dolar"
                      name="valorFreteDolar"
                      label="Valor Frete Internacional em Dólar"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-frete-real"
                      name="valorFreteReal"
                      label="Valor Frete Internacional em Reais"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>

                  {/* Linha 6: Seguro */}
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-moeda-seguro"
                      label="Moeda Seguro"
                      value={getMoedaLabel(adicao?.moeda)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-seguro-moeda"
                      name="valorSeguroMoeda"
                      label="Valor Seguro Internacional na Moeda"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-seguro-dolar"
                      name="valorSeguroDolar"
                      label="Valor Seguro Internacional em Dólar"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-seguro-real"
                      name="valorSeguroReal"
                      label="Valor Seguro Internacional em Reais"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>

                  {/* Linha 7: Deduções */}
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-moeda-deducoes"
                      label="Moeda Deduções"
                      value={getMoedaLabel(adicao?.moeda)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-deducao-moeda"
                      name="valorDeducaoMoeda"
                      label="Valor Deduções na Moeda"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-deducao-dolar"
                      name="valorDeducaoDolar"
                      label="Valor Deduções em Dólar"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-deducao-real"
                      name="valorDeducaoReal"
                      label="Valor Deduções em Reais"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>

                  {/* Linha 8: Acréscimos */}
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXTextField
                      id="f-moeda-acrescimos"
                      label="Moeda Acréscimos"
                      value={getMoedaLabel(adicao?.moeda)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-acrescimo-moeda"
                      name="valorAcrescimoMoeda"
                      label="Valor Acréscimos na Moeda"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-acrescimo-dolar"
                      name="valorAcrescimoDolar"
                      label="Valor Acréscimos em Dólar"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <QCXFinalAlternativeCurrencyField
                      id="f-valor-acrescimo-real"
                      name="valorAcrescimoReal"
                      label="Valor Acréscimos em Reais"
                      disabled
                      submitSucceeded={submitSucceeded}
                      scale={7}
                    />
                  </Grid>



                  <Grid item xs={12} sm={6}>
                    <QCXTextField
                      id="f-valor-cif"
                      label="Valor Total do CIF"
                      value={fmtR(adicao?.valorCifReal)}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <QCXSelectIncotermCondicaoVendaAutocomplete
                      id="f-incoterm"
                      name="incotermCondicaoVenda.id"
                      label="INCOTERM"
                      initialValues={values}
                      disabled
                    />
                  </Grid>


                  {/* ── BLOCO 4 — TRIBUTOS ── */}
                  <Grid item xs={12}>
                    <QCXFormTitle title="Tributos" />
                  </Grid>

                  {/* II */}
                  <Grid item xs={12} md={6}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>II</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXSelectRegimeTributacaoAutocomplete
                            id="f-regime-ii"
                            name="ii.regimeTributacao.id"
                            label={t('com.muralis.qcx.regimeTributacao')}
                            disabled
                            initialValues={values}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-bc-ii"
                            name="ii.baseCalculoReal"
                            label="Base de Cálculo em Reais"
                            disabled
                            adorned
                            intl={intl}
                            submitSucceeded={submitSucceeded}
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalPercentualField
                            id="f-aliq-ii"
                            name="ii.aliquota"
                            label="Alíquota ad valorem"
                            disabled
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-devido-ii"
                            name="ii.valorDevidoReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-recolher-ii"
                            name="ii.valorRecolherReal"
                            label="Valor a Recolher em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* IPI */}
                  <Grid item xs={12} md={6}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>IPI</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXSelectRegimeTributacaoAutocomplete
                            id="f-regime-ipi"
                            name="ipi.regimeTributacao.id"
                            label={t('com.muralis.qcx.regimeTributacao')}
                            disabled
                            initialValues={values}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-bc-ipi"
                            name="ipi.baseCalculoReal"
                            label="Base de Cálculo em Reais"
                            disabled
                            adorned
                            intl={intl}
                            submitSucceeded={submitSucceeded}
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalPercentualField
                            id="f-aliq-ipi"
                            name="ipi.aliquota"
                            label="Alíquota ad valorem"
                            disabled
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-devido-ipi"
                            name="ipi.valorDevidoReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-recolher-ipi"
                            name="ipi.valorRecolherReal"
                            label="Valor a Recolher em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* PIS / PASEP */}
                  <Grid item xs={12} md={6}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>PIS / PASEP</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXSelectRegimeTributacaoAutocomplete
                            id="f-regime-pis"
                            name="pisCofins.regimeTributacao.id"
                            label={t('com.muralis.qcx.regimeTributacao')}
                            disabled
                            initialValues={values}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-bc-pis"
                            name="pisCofins.baseCalculoReal"
                            label="Base de Cálculo em Reais"
                            disabled
                            adorned
                            intl={intl}
                            submitSucceeded={submitSucceeded}
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalPercentualField
                            id="f-aliq-pis"
                            name="pisCofins.aliquotaPisPasep"
                            label="Alíquota ad valorem"
                            disabled
                            scale={5}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-devido-pis"
                            name="pisCofins.valorDevidoPisPasepReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-recolher-pis"
                            name="pisCofins.valorRecolherPisPasepReal"
                            label="Valor a Recolher em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* COFINS */}
                  <Grid item xs={12} md={6}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>COFINS</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXSelectRegimeTributacaoAutocomplete
                            id="f-regime-cofins"
                            name="pisCofins.regimeTributacao.id"
                            label={t('com.muralis.qcx.regimeTributacao')}
                            disabled
                            initialValues={values}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-bc-cofins"
                            name="pisCofins.baseCalculoReal"
                            label="Base de Cálculo em Reais"
                            disabled
                            adorned
                            intl={intl}
                            submitSucceeded={submitSucceeded}
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalPercentualField
                            id="f-aliq-cofins"
                            name="pisCofins.aliquotaCofins"
                            label="Alíquota ad valorem"
                            disabled
                            scale={5}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-devido-cofins"
                            name="pisCofins.valorDevidoCofinsReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-recolher-cofins"
                            name="pisCofins.valorRecolherCofinsReal"
                            label="Valor a Recolher em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* IBS */}
                  <Grid item xs={12} md={6}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>IBS</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXSelectRegimeTributacaoAutocomplete
                            id="f-regime-ibs"
                            name="ibs.regimeTributacao.id"
                            label={t('com.muralis.qcx.regimeTributacao')}
                            disabled
                            initialValues={values}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-bc-ibs"
                            name="ibs.baseCalculoReal"
                            label="Base de Cálculo em Reais"
                            disabled
                            adorned
                            intl={intl}
                            submitSucceeded={submitSucceeded}
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalPercentualField
                            id="f-aliq-ibs"
                            name="ibs.aliquota"
                            label="Alíquota ad valorem"
                            disabled
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-devido-ibs"
                            name="ibs.valorDevidoReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-recolher-ibs"
                            name="ibs.valorRecolherReal"
                            label="Valor a Recolher em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* CBS */}
                  <Grid item xs={12} md={6}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>CBS</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXSelectRegimeTributacaoAutocomplete
                            id="f-regime-cbs"
                            name="cbs.regimeTributacao.id"
                            label={t('com.muralis.qcx.regimeTributacao')}
                            disabled
                            initialValues={values}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-bc-cbs"
                            name="cbs.baseCalculoReal"
                            label="Base de Cálculo em Reais"
                            disabled
                            adorned
                            intl={intl}
                            submitSucceeded={submitSucceeded}
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalPercentualField
                            id="f-aliq-cbs"
                            name="cbs.aliquota"
                            label="Alíquota ad valorem"
                            disabled
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-devido-cbs"
                            name="cbs.valorDevidoReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-recolher-cbs"
                            name="cbs.valorRecolherReal"
                            label="Valor a Recolher em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* ICMS */}
                  <Grid item xs={12} md={6}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>ICMS</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXSelectRegimeTributacaoAutocomplete
                            id="f-regime-icms"
                            name="icms.regimeTributacao.id"
                            label={t('com.muralis.qcx.regimeTributacao')}
                            disabled
                            initialValues={values}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-bc-icms"
                            name="icms.baseCalculoReal"
                            label="Base de Cálculo em Reais"
                            disabled
                            adorned
                            intl={intl}
                            submitSucceeded={submitSucceeded}
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalPercentualField
                            id="f-aliq-icms"
                            name="icms.aliquota"
                            label="Alíquota ad valorem"
                            disabled
                            scale={7}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-devido-icms"
                            name="icms.valorDevidoReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-recolher-icms"
                            name="icms.valorRecolherReal"
                            label="Valor a Recolher em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>


                  {/* ── BLOCO 5 — CAMPOS COMPLEMENTARES ── */}
                  <Grid item xs={12}>
                    <QCXFormTitle title="Campos Complementares" />
                  </Grid>

                  {/* Taxa SISCOMEX */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>Taxa SISCOMEX</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-taxa-siscomex"
                            name="taxaSiscomex.valorReal"
                            label="Valor da Taxa em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Despesas Acessórias */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>Despesas Acessórias</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-despesas-acessorias"
                            name="valorDespesaAcessoriaReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* Antidumping */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>Antidumping</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-antidumping"
                            name="antidumping.valorDevidoReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                  {/* AFRMM */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box className={classes.groupBox}>
                      <Typography className={classes.groupTitle}>AFRMM</Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!adicao?.afrmm?.afrmmIsento}
                                disabled
                                color="primary"
                                size="small"
                              />
                            }
                            label={<Typography variant="body2">AFRMM Isento</Typography>}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <QCXFinalAlternativeCurrencyField
                            id="f-afrmm-devido"
                            name="afrmm.valorDevidoReal"
                            label="Valor Devido em Reais"
                            disabled
                            adorned
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>

                </>
              )}
            </QCXCustomRegistrationTabControlTemplate>

          </>
        )}
      </QCXFormStepWizard>
    </>
  );
};


export default AdicaoResumoFormStepWizard;
